import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay, take } from 'rxjs/operators';
import { CaseUserStatus } from 'src/app/models/referenceData';
import { AlertDetailService } from '../alert-detail/alert-detail.service';
import { AlertService } from '../alert/alert.service';

export interface CaseSummary {
  id: string;
  orderNumber?: string;
  status?: { userStatusId?: number };
}

export interface CaseStatus {
  hasCase: boolean;
  orderNumber: string | null;
  statusId: number;
  statusLabel: string;
}

@Injectable({ providedIn: 'root' })
export class CaseStatusService {
  private statuses$?: Observable<CaseUserStatus[]>;

  constructor(
    private alertDetailService: AlertDetailService,
    private alertService: AlertService,
    private translate: TranslateService,
  ) {}

  caseStatusFor(alertId: string): Observable<CaseStatus> {
    return combineLatest([this.caseFor(alertId), this.userStatuses()]).pipe(
      take(1),
      map(([activeCase, refData]) => this.resolve(activeCase, refData)),
    );
  }

  caseStatusesFor(alertIds: string[]): Observable<Map<string, CaseStatus>> {
    if (!alertIds.length) return of(new Map());
    return combineLatest([
      this.userStatuses(),
      forkJoin(alertIds.map(id => this.caseFor(id).pipe(map(c => [id, c] as const)))),
    ]).pipe(
      take(1),
      map(([refData, entries]) =>
        new Map(entries.map(([id, c]) => [id, this.resolve(c, refData)] as const)),
      ),
    );
  }

  private caseFor(alertId: string): Observable<CaseSummary | null> {
    return this.alertDetailService.getCaseByAlertId(alertId).pipe(
      map((cases: CaseSummary[]) => (cases?.length ? cases[0] : null)),
      catchError(() => of(null)),
    );
  }

  userStatuses(): Observable<CaseUserStatus[]> {
    if (!this.statuses$) {
      this.statuses$ = this.alertService.getCaseUserStatuses().pipe(
        catchError(() => of([] as CaseUserStatus[])),
        shareReplay(1),
      );
    }
    return this.statuses$;
  }

  private resolve(activeCase: CaseSummary | null, refData: CaseUserStatus[]): CaseStatus {
    const statusId = activeCase?.status?.userStatusId ?? 0;
    const key = refData.find(s => s.id === statusId)?.name ?? '@notStarted';
    return {
      hasCase: !!activeCase,
      orderNumber: activeCase?.orderNumber ?? null,
      statusId,
      statusLabel: this.translate.instant(key),
    };
  }
}
